import { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useArbeitspakete } from '../../../../api/hooks/useArbeitspakete';
import { useProjectMilestones } from '../../../../api/hooks/useProjects';
import { useMeetings } from '../../../../api/hooks/useMeetings';
import { useOffenePunkte } from '../../../../api/hooks/useOffenePunkte';
import { PmInfoBox } from '../../../../components/pm';
import {
  PmProjectGantt,
  type GanttArbeitspaket, type GanttMilestone, type GanttMeeting, type GanttOffenerPunkt,
} from '../PmProjectGantt';

interface RawMilestone {
  id: string;
  name?: string | null;
  titel?: string | null;
  deadline?: string | null;
  date?: string | null;
  faellig?: string | null;
}

/**
 * Mini-Gantt für das Dashboard — bettet die bestehende read-only-Gantt-Ansicht
 * ein (kein onApMove → nicht editierbar) und verlinkt auf die Vollansicht.
 */
export function DashboardMiniGantt({ projectId }: { projectId: string }) {
  const { t } = useTranslation('projects');
  const navigate = useNavigate();
  const { data: apResp } = useArbeitspakete(projectId);
  const { data: msResp } = useProjectMilestones(projectId);
  const { data: mtResp } = useMeetings(projectId);
  const { data: opResp } = useOffenePunkte(projectId);

  const aps: GanttArbeitspaket[] = useMemo(
    () => ((apResp as unknown as { data?: GanttArbeitspaket[] } | undefined)?.data ?? []),
    [apResp],
  );

  const milestones: GanttMilestone[] = useMemo(() => {
    const raw = (msResp as unknown as { data?: RawMilestone[] } | undefined)?.data ?? [];
    return raw.map((m) => ({
      id: m.id,
      name: m.name ?? m.titel ?? '—',
      date: m.deadline ?? m.date ?? m.faellig ?? null,
    }));
  }, [msResp]);

  const meetings: GanttMeeting[] = useMemo(
    () => ((mtResp as unknown as { data?: GanttMeeting[] } | undefined)?.data ?? []),
    [mtResp],
  );

  const offenePunkte: GanttOffenerPunkt[] = useMemo(
    () => ((opResp as unknown as { data?: GanttOffenerPunkt[] } | undefined)?.data ?? []),
    [opResp],
  );

  if (aps.length === 0) {
    return (
      <PmInfoBox variant="info" title={t('dashboard.ganttEmpty', 'Noch keine Arbeitspakete')}>
        {t('dashboard.ganttEmptyHint', 'Sobald Arbeitspakete angelegt sind, erscheint hier die Projektplan-Übersicht.')}
      </PmInfoBox>
    );
  }

  return (
    <Stack spacing={1}>
      <PmProjectGantt
        arbeitspakete={aps}
        milestones={milestones}
        meetings={meetings}
        offenePunkte={offenePunkte}
      />
      <Box
        component="a"
        onClick={() => navigate(`/projects/${projectId}/projektplan`)}
        sx={{
          alignSelf: 'flex-end',
          cursor: 'pointer',
          color: 'var(--pm-fg-accent)',
          fontSize: '0.78rem',
          textDecoration: 'underline',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
        }}
      >
        {t('dashboard.fullView', 'Vollansicht')}
        <ArrowForwardIcon sx={{ fontSize: 14 }} />
      </Box>
    </Stack>
  );
}
